import { Box } from '@oms/ui-box';
import { Button } from '@oms/ui-button';
import { DateInput } from '@oms/ui-date-input';
import { Field, RenderField } from '@oms/ui-field';
import { Field as FField } from 'react-final-form';
import { Select } from '@oms/ui-select';
import { Stack } from '@oms/ui-stack';
import { FieldArray } from 'react-final-form-arrays';
import { countryOptions } from 'utils/locales';
import { required } from '../validate';
import { positionOptions } from './utils';
import { checkIsPDFRender } from 'utils/expediencyTest';
import { useLocation } from 'react-router-dom';
import PdfTextInput from 'components/FormComponents/PdfTextInput';

const initialPositions: any[] = [{}];

type Props = { name: string };

const PersonArray = ({ name }: Props) => {
  const location = useLocation();
  const isPDF = checkIsPDFRender(location);
  return (
    <FieldArray name={name}>
      {({ fields }) => (
        <>
          {fields.map((personFieldName: string, index: number) => (
            <Stack
              my={4}
              as="fieldset"
              key={personFieldName}
              gap={2}
              border="sm"
              p={4}
            >
              <Box>
                <label>Person nr. {index + 1}</label>
                {index !== 0 && (
                  <Button ml={4} size="xs" onClick={() => fields.remove(index)}>
                    Fjern person
                  </Button>
                )}
              </Box>

              <PdfTextInput
                name={`${personFieldName}.personalia.givenName`}
                label="Fornavn"
                isPDFRender={isPDF}
              />

              <PdfTextInput
                name={`${personFieldName}.personalia.surName`}
                label="Etternavn"
                isPDFRender={isPDF}
              />

              <PdfTextInput
                name={`${personFieldName}.personalia.address`}
                label="Adresse"
                autoComplete="address"
                isPDFRender={isPDF}
              />

              <PdfTextInput
                name={`${personFieldName}.personalia.email`}
                label="E-post"
                autoComplete="email"
                isPDFRender={isPDF}
              />

              <PdfTextInput
                name={`${personFieldName}.personalia.phoneNumber`}
                label="Mobilnummer"
                isPDFRender={isPDF}
              />

              <FieldArray
                name={`${personFieldName}.positionList`}
                initialValue={initialPositions}
              >
                {({ fields: positions }) => (
                  <Box>
                    {positions.map((positionFieldName, index) => (
                      <Stack gap={2} mt={8}>
                        <Box>
                          <label>Stilling nr. {index + 1}</label>
                          {index !== 0 && (
                            <Button
                              ml={4}
                              size="xs"
                              onClick={() => positions.remove(index)}
                            >
                              Fjern stilling
                            </Button>
                          )}
                        </Box>
                        <Field
                          name={`${positionFieldName}.position`}
                          as={Select}
                          label="Offentlig stilling/politisk verv"
                          items={positionOptions}
                          validate={required}
                        />

                        <PdfTextInput
                          name={`${positionFieldName}.title`}
                          label="Stillingstittel"
                          isPDFRender={isPDF}
                        />

                        <PdfTextInput
                          name={`${positionFieldName}.organization`}
                          label="Arbeidsgiver"
                          isPDFRender={isPDF}
                        />

                        {isPDF ? (
                          <>
                            <FField
                              name={`${positionFieldName}.fromDate`}
                              component={props => (
                                <RenderField
                                  name={props.input.name}
                                  as={DateInput}
                                  dateFormat="yyyy.MM.dd"
                                  label="Start"
                                  value={new Date(props.input.value)}
                                />
                              )}
                            />
                            <FField
                              name={`${positionFieldName}.toDate`}
                              component={props => {
                                if (!props?.input?.value) return null;
                                return (
                                  <RenderField
                                    name={props?.input?.name}
                                    dateFormat="yyyy.MM.dd"
                                    as={DateInput}
                                    label="Slutt"
                                    value={new Date(props?.input?.value)}
                                  />
                                );
                              }}
                            />
                          </>
                        ) : (
                          <>
                            <Field
                              name={`${positionFieldName}.fromDate`}
                              as={DateInput}
                              label="Start"
                              dateFormat="yyyy.MM.dd"
                              validate={required}
                            />
                            <Field
                              name={`${positionFieldName}.toDate`}
                              as={DateInput}
                              label="Slutt"
                              dateFormat="yyyy.MM.dd"
                            />
                          </>
                        )}
                        <Field
                          name={`${positionFieldName}.country`}
                          as={Select}
                          label="Land"
                          validate={required}
                          items={countryOptions}
                        />
                      </Stack>
                    ))}
                    <Button
                      type="button"
                      variant="tertiary"
                      onClick={() => positions.push({})}
                    >
                      Legg til stilling
                    </Button>
                  </Box>
                )}
              </FieldArray>
            </Stack>
          ))}
          <Button
            type="button"
            variant="tertiary"
            onClick={() => fields.push({})}
          >
            Legg til person
          </Button>
        </>
      )}
    </FieldArray>
  );
};

export default PersonArray;
